import type { InboundLineRow } from "./inboundApi";

function isCollapsibleOrderLine(line: InboundLineRow): boolean {
  return line.line_kind == null || line.line_kind === "order_line";
}

export function inboundSkuKey(sku: string): string {
  return sku.trim().toLowerCase();
}

/** Order lines sort alphabetically by SKU; ad-hoc lines stay at the end in receipt-item order. */
export function sortInboundLinesStable(lines: readonly InboundLineRow[]): InboundLineRow[] {
  return [...lines].sort((left, right) => {
    const leftIsAdHoc = left.line_kind === "unlisted_sku" || left.line_kind === "non_sku";
    const rightIsAdHoc = right.line_kind === "unlisted_sku" || right.line_kind === "non_sku";
    if (leftIsAdHoc !== rightIsAdHoc) return leftIsAdHoc ? 1 : -1;
    const skuCompare = left.sku.localeCompare(right.sku, undefined, { sensitivity: "base" });
    if (skuCompare !== 0) return skuCompare;
    if (!leftIsAdHoc && !rightIsAdHoc) {
      const leftLineNumber = left.line_number ?? Number.MAX_SAFE_INTEGER;
      const rightLineNumber = right.line_number ?? Number.MAX_SAFE_INTEGER;
      if (leftLineNumber !== rightLineNumber) return leftLineNumber - rightLineNumber;
    }
    return left.source_item_id.localeCompare(right.source_item_id);
  });
}

/** One horizontal tile per purchase-order SKU; duplicate PO lines share the canonical line. */
export function collapseInboundLinesBySku(lines: readonly InboundLineRow[]): InboundLineRow[] {
  const sorted = sortInboundLinesStable(lines);
  const collapsed: InboundLineRow[] = [];
  const seenSkus = new Set<string>();
  for (const line of sorted) {
    if (isCollapsibleOrderLine(line)) {
      const key = inboundSkuKey(line.sku);
      if (seenSkus.has(key)) continue;
      seenSkus.add(key);
    }
    collapsed.push(line);
  }
  return collapsed;
}

export function canonicalInboundLineForSku(
  sku: string,
  lines: readonly InboundLineRow[],
): InboundLineRow | null {
  const key = inboundSkuKey(sku);
  const matches = lines.filter(
    (line) => isCollapsibleOrderLine(line) && inboundSkuKey(line.sku) === key,
  );
  if (matches.length === 0) return null;
  return sortInboundLinesStable(matches)[0] ?? null;
}

export function inboundLineIdsForSku(
  sku: string,
  lines: readonly InboundLineRow[],
): string[] {
  const key = inboundSkuKey(sku);
  const matches = lines.filter(
    (line) => isCollapsibleOrderLine(line) && inboundSkuKey(line.sku) === key,
  );
  return sortInboundLinesStable(matches).map((line) => line.source_item_id);
}

export function inboundLinesShareSku(
  left: InboundLineRow,
  right: InboundLineRow | null | undefined,
): boolean {
  if (right == null) return false;
  if (!isCollapsibleOrderLine(left) || !isCollapsibleOrderLine(right)) {
    return left.source_item_id === right.source_item_id;
  }
  return inboundSkuKey(left.sku) === inboundSkuKey(right.sku);
}

export function resolveInboundReceiveLineId(
  line: InboundLineRow,
  lines: readonly InboundLineRow[],
): string {
  if (!isCollapsibleOrderLine(line)) return line.source_item_id;
  return canonicalInboundLineForSku(line.sku, lines)?.source_item_id ?? line.source_item_id;
}
